import { Flex, Input, InputGroup, Field, Box } from "@chakra-ui/react";
import { Tooltip } from "@/components/ui/tooltip";
import { t } from "i18next";
import { Control, useController } from "react-hook-form";
import { NumericFormat, NumberFormatValues } from "react-number-format";
import { InvoiceFormData, NumericStringFieldKeys } from "@/types";
import { PreFilledBadges } from "../../invoiceAttachmentSection/PreFilledInformation";
import { useCallback } from "react";
import { InfoCircleIcon } from "@digital-services-gard-as/anchor-icons";
import { Button, ErrorMessage } from "@digital-services-gard-as/anchor-react";

type FormNumberInputProps = {
    label: string;
    id: NumericStringFieldKeys;
    isRequired: boolean;
    errorMessage: string | undefined;
    control: Control<InvoiceFormData>;
    currency: string | undefined;
    customCompareValue?: string;
    allowNegative?: boolean;
    isDisabled?: boolean;
    isHidden?: boolean;
};

export const FormNumberInput = ({
    label,
    id,
    errorMessage,
    isRequired,
    control,
    currency,
    customCompareValue,
    allowNegative = true,
    isDisabled = false,
    isHidden = false
}: FormNumberInputProps) => {
    const { field } = useController({
        name: id,
        control
    });

    const handleChange = useCallback(
        (values: NumberFormatValues) => {
            const value = values.value || "0";
            field.onChange(value);
        },
        [field]
    );

    return (
        <Field.Root invalid={Boolean(errorMessage)} required={isRequired} hidden={isHidden} w="100%" maxW="320px">
            <Flex gap="0.5rem" alignItems="center">
                <Field.Label fontWeight="anchor.bold">{label}</Field.Label>
                <PreFilledBadges fieldName={id} isDisabled={isDisabled} customCompareValue={customCompareValue} />
            </Flex>
            <Flex alignItems="center" gap="0.2rem" w="100%">
                <InputGroup
                    minW={0}
                    flex={isDisabled ? "1 1 auto" : "1 1 0%"}
                    w="100%"
                    endAddon={<Box bg="anchor.navy.50">{currency}</Box>}
                >
                    <Input
                        id={id}
                        autoComplete="off"
                        placeholder="0"
                        data-lpignore="true"
                        disabled={isDisabled}
                        w="100%"
                        asChild
                    >
                        <NumericFormat
                            getInputRef={field.ref}
                            value={field.value || ""}
                            onValueChange={handleChange}
                            onBlur={field.onBlur}
                            thousandSeparator=","
                            decimalSeparator="."
                            allowNegative={allowNegative}
                            decimalScale={2}
                        />
                    </Input>
                </InputGroup>
                {isDisabled && (
                    <Box ml="0.5rem" flexShrink={0}>
                        <Tooltip
                            showArrow
                            content={t("invoices:createInvoice.feesAndExpenses.vatNotApplicable")}
                            positioning={{
                                placement: "top"
                            }}
                        >
                            <span>
                                <Button
                                    variant="icon-only"
                                    icon={<InfoCircleIcon />}
                                    style={{
                                        backgroundColor: "var(--a-red-50)",
                                        color: "var(--a-red-600)"
                                    }}
                                    aria-label={t("invoices:createInvoice.feesAndExpenses.vatNotApplicable")}
                                    tabIndex={0}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Flex>
            <ErrorMessage size="sm" data-testid={`${id}Error`}>
                {errorMessage}
            </ErrorMessage>
        </Field.Root>
    );
};
